import {
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import {
  GalaxyAbstractFilterInput,
  GalaxyFilterOperator,
  GalaxyFilterValueInterface,
} from '@vendasta/galaxy/filter/chips';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatChipGrid, MatChipInput, MatChipInputEvent, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CrmInjectionToken } from '../../../tokens-and-interfaces';
import { CrmPipelineService, Stage } from '../../../shared-services/crm-services';
import { map } from 'rxjs/operators';

@Component({
  template: `
    @defer (when loadingValues() === false) {
      @if (singleInput()) {
        <glxy-form-field bottomSpacing="none">
          <input
            type="text"
            [placeholder]="'TABLE_FILTERS.SELECT_EXPRESSION_PLACEHOLDER' | translate"
            matInput
            [formControl]="formControl"
            [matAutocomplete]="auto"
          />
          <mat-autocomplete
            #auto="matAutocomplete"
            (optionSelected)="selected($event)"
            [displayWith]="displayFn.bind(this)"
          >
            @for (option of filteredOptions(); track option.id) {
              <mat-option [value]="$safeNavigationMigration(option?.id)">
                {{ option?.name }}
              </mat-option>
            }
          </mat-autocomplete>
        </glxy-form-field>
      } @else {
        <glxy-form-field bottomSpacing="none">
          <mat-chip-grid #stageGrid aria-label="Stage selection">
            @for (stage of selectedStages(); track stage.id) {
              <mat-chip-row (removed)="remove(stage.id)">
                {{ stage.name }}
                <button matChipRemove [attr.aria-label]="'remove ' + stage.name">
                  <mat-icon>cancel</mat-icon>
                </button>
              </mat-chip-row>
            }
            <input
              matInput
              #stageInput
              [placeholder]="'TABLE_FILTERS.SELECT_EXPRESSION_PLACEHOLDER' | translate"
              [formControl]="formControl"
              [matChipInputFor]="stageGrid"
              [matAutocomplete]="auto"
              [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
              [matChipInputAddOnBlur]="false"
              (matChipInputTokenEnd)="add($event)"
            />
          </mat-chip-grid>
          <mat-autocomplete #auto="matAutocomplete" (optionSelected)="selected($event)">
            @for (option of filteredOptions(); track option.id) {
              <mat-option [value]="$safeNavigationMigration(option?.id)">
                {{ option?.name }}
              </mat-option>
            }
          </mat-autocomplete>
        </glxy-form-field>
      }
    } @placeholder {
      <mat-spinner [diameter]="24"></mat-spinner>
    }
  `,
  providers: [
    {
      provide: GalaxyAbstractFilterInput,
      useExisting: forwardRef(() => CrmOpportunityStageFilterInputComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    GalaxyFormFieldModule,
    MatAutocompleteModule,
    MatChipGrid,
    MatChipInput,
    MatChipRemove,
    MatChipRow,
    MatIcon,
    MatProgressSpinner,
  ],
})
export class CrmOpportunityStageFilterInputComponent implements GalaxyAbstractFilterInput {
  @Input() set initialValue(value: GalaxyFilterValueInterface[]) {
    const stageIds = (value ?? []).map((v) => v.string ?? '');
    this.selectedStageIds.set(stageIds);
    const singleInput = this.singleInput();
    if (stageIds.length > 0 && singleInput) {
      this.formControl.setValue(stageIds[0]);
    }
  }

  private _operator?: GalaxyFilterOperator;
  @Input() set operator(value: GalaxyFilterOperator) {
    this._operator = value;
    const isMultiInput =
      this._operator == GalaxyFilterOperator.FILTER_OPERATOR_IS_ANY ||
      this._operator == GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_ANY;
    if (isMultiInput) {
      this.formControl.setValue('');
    } else {
      const selectedStageIds = this.selectedStageIds();
      if (selectedStageIds.length > 0) {
        this.formControl.setValue(selectedStageIds[0]);
        if (selectedStageIds.length > 1) {
          this.selectedStageIds.set([selectedStageIds[0]]);
        }
      }
    }
    this.singleInput.set(!isMultiInput);
  }

  @Output() valueChanges = new EventEmitter<GalaxyFilterValueInterface[]>();

  @ViewChild('stageInput') stageInput?: ElementRef<HTMLInputElement>;

  private readonly config = inject(CrmInjectionToken);
  private readonly pipelineService = inject(CrmPipelineService);

  protected readonly formControl = new FormControl<Stage | string>('', { nonNullable: true });
  protected readonly searchTerm = toSignal(this.formControl.valueChanges, { initialValue: '' });
  protected readonly loadingValues = signal(true);
  protected readonly availableOptions = signal<Stage[]>([]);
  protected readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  protected readonly singleInput = signal(true);

  protected readonly filteredOptions = computed(() => {
    const searchTerm = this.searchTerm();
    const availableOptions = this.availableOptions();
    const filterTerm = typeof searchTerm === 'string' ? searchTerm : searchTerm?.id;
    return availableOptions.filter((o) => this.stageFilterMatches(o, filterTerm));
  });

  private readonly selectedStageIds = signal<string[]>([]);
  protected readonly selectedStages = computed(() => {
    const selectedIds = this.selectedStageIds();
    const availableOptions = this.availableOptions();
    const selectedStages = selectedIds
      .map((id) => availableOptions.find((option) => option.id === id))
      .filter((s) => !!s);
    return selectedStages;
  });

  constructor() {
    effect(() => {
      const selectedStageIds = this.selectedStageIds();
      this.valueChanges.emit(selectedStageIds.map((s) => ({ string: s })));
    });

    this.pipelineService
      .listPipelines$('Opportunity')
      .pipe(
        takeUntilDestroyed(),
        map((pipelines) => {
          const pipelineId = this.pipelineService.getCurrentPipelineId();
          if (pipelineId) {
            const pipeline = pipelines.find((p) => p.id === pipelineId);
            return pipeline?.stages ?? [];
          }
          const allStages: Stage[] = [];
          for (const pipeline of pipelines) {
            allStages.push(...pipeline.stages);
          }
          return allStages;
        }),
      )
      .subscribe((stages) => {
        this.availableOptions.set(stages);
        this.loadingValues.set(false);
      });
  }

  clearValue() {
    this.formControl.setValue('');
  }

  displayFn(stageId?: string): string {
    if (!stageId) {
      return '';
    }
    const foundStage = this.availableOptions().find((o) => o.id === stageId);
    return foundStage?.name || stageId;
  }

  add(event: MatChipInputEvent) {
    const currentStages = this.selectedStageIds();
    const exists = currentStages.some((s) => s === event?.value);
    if (event?.value && !exists) {
      this.selectedStageIds.set([...currentStages, event.value]);
    }
    event.chipInput?.clear();
    this.formControl.setValue('');
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const singleInput = this.singleInput();
    const currentStages = this.selectedStageIds();
    const exists = currentStages.some((s) => s === event.option.value);
    if (event.option.value && !exists) {
      if (singleInput) {
        this.selectedStageIds.set([event.option.value]);
      } else {
        this.selectedStageIds.set([...currentStages, event.option.value]);
      }
    }
    if (!singleInput && this.stageInput?.nativeElement) {
      this.stageInput.nativeElement.value = '';
      this.formControl.setValue('');
    }
  }

  remove(stageId: string) {
    const currentStages = this.selectedStageIds();
    const remainingStages = currentStages.filter((s) => s !== stageId);
    this.selectedStageIds.set([...remainingStages]);
  }

  private stageFilterMatches(stage: Stage, filterTerm: string): boolean {
    return stage.id.includes(filterTerm) || stage.name?.toLowerCase().includes(filterTerm.toLowerCase());
  }
}
